import { useState, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import api from '../api';
import { useLang } from '../context/LangContext';

const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const YEAR_COLORS = ['#0d9488', '#2563eb', '#ea580c', '#9333ea', '#16a34a', '#dc2626'];
const GROUP_META = [
  ['afname', 'Afname', '#0d9488'],
  ['injectie', 'Injectie', '#84cc16'],
  ['groene_stroom', 'Groene stroom', '#22c55e'],
  ['wkk', 'WKK', '#10b981'],
  ['netwerk', 'Netwerk', '#6366f1'],
  ['toeslagen', 'Toeslagen', '#f59e0b'],
  ['andere', 'Andere', '#94a3b8'],
];

function ChartCard({ title, children, sub }) {
  return (
    <div className="chart-card mb-4">
      <div className="chart-title">{title}{sub && <span className="text-muted-sm ms-2">{sub}</span>}</div>
      <div style={{ padding: '12px 12px 4px' }}>{children}</div>
    </div>
  );
}

// rows [{year, month(1-12), energy_type, consumption, cost}] → [{month:'jan', 2024:.., 2025:..}]
function pivotByYear(rows, energyType, metric) {
  const base = MONTHS.map((m, i) => ({ month: m, _m: i + 1 }));
  rows.filter(r => r.energy_type === energyType).forEach(r => {
    const row = base[Number(r.month) - 1];
    if (row) row[r.year] = r[metric] == null ? null : Number(r[metric]);
  });
  return base;
}

// componenten [{month, energy_type, line_group, amount}] → [{month, afname, netwerk, ...}]
function pivotComponents(rows, energyType) {
  const map = {};
  rows.filter(r => r.energy_type === energyType).forEach(r => {
    map[r.month] = map[r.month] || { month: r.month };
    map[r.month][r.line_group] = Number(r.amount || 0);
  });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

// eenheidsprijs [{month, energy_type, tarief, unit_price}] → [{month, piek, dal}]
function pivotUnit(rows, energyType) {
  const map = {};
  rows.filter(r => r.energy_type === energyType).forEach(r => {
    map[r.month] = map[r.month] || { month: r.month };
    map[r.month][r.tarief] = r.unit_price == null ? null : Number(r.unit_price);
  });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

export default function AnalyticsPage() {
  const { t } = useLang();
  const [comparison, setComparison] = useState([]);
  const [components, setComponents] = useState([]);
  const [unit, setUnit]             = useState([]);
  const [injection, setInjection]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [type, setType]             = useState('elektriciteit');
  const [metric, setMetric]         = useState('consumption');

  useEffect(() => {
    Promise.all([
      api.get('/api/analytics/comparison'),
      api.get('/api/analytics/components'),
      api.get('/api/analytics/unit-price-detail'),
      api.get('/api/analytics/injection'),
    ]).then(([c, comp, u, inj]) => {
      setComparison(c.data); setComponents(comp.data); setUnit(u.data); setInjection(inj.data);
    }).finally(() => setLoading(false));
  }, []);

  const years = Array.from(new Set(comparison.map(r => Number(r.year)))).sort();
  const cmpData  = pivotByYear(comparison, type, metric);
  const compData = pivotComponents(components, type);
  const unitData = pivotUnit(unit, type);

  if (loading) {
    return (
      <>
        <div className="page-header"><h2>{t('analytics_title')}</h2></div>
        <div className="page-body text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header"><h2>{t('analytics_title')}</h2></div>
      <div className="page-body">
        {/* Energietype-keuze geldt voor alle grafieken hieronder */}
        <div className="btn-group btn-group-sm mb-3">
          <button className={`btn btn-outline-primary${type === 'elektriciteit' ? ' active' : ''}`}
            onClick={() => setType('elektriciteit')}>{t('type_elektriciteit')}</button>
          <button className={`btn btn-outline-primary${type === 'gas' ? ' active' : ''}`}
            onClick={() => setType('gas')}>{t('type_gas')}</button>
        </div>

        {/* Jaarvergelijking */}
        <ChartCard title={`${t('an_comparison')} — ${t(`type_${type}`)}`}
          sub={metric === 'consumption' ? (type === 'gas' ? 'm³' : 'kWh') : '€'}>
          <div className="btn-group btn-group-sm mb-2">
            <button className={`btn btn-outline-secondary${metric === 'consumption' ? ' active' : ''}`}
              onClick={() => setMetric('consumption')}>{t('an_consumption')}</button>
            <button className={`btn btn-outline-secondary${metric === 'cost' ? ' active' : ''}`}
              onClick={() => setMetric('cost')}>{t('inv_amount')}</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cmpData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
              {years.map((y, i) => (
                <Line key={y} type="monotone" dataKey={y} name={String(y)}
                  stroke={YEAR_COLORS[i % YEAR_COLORS.length]} dot={false} strokeWidth={2} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Kosten per component */}
        <ChartCard title={`${t('an_components')} — ${t(`type_${type}`)}`} sub="€">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={compData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
              {GROUP_META.map(([g, label, color]) => (
                <Bar key={g} dataKey={g} name={label} stackId="a" fill={color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Eenheidsprijs piek/dal — enkel elektriciteit */}
        {type === 'elektriciteit' && (
          <ChartCard title={t('an_unit_detail')} sub="€/kWh">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={unitData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="piek" name="Piek" stroke="#2563eb" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="dal" name="Dal" stroke="#0d9488" dot={false} strokeWidth={2} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Afname vs injectie — enkel elektriciteit */}
        {type === 'elektriciteit' && (
          <ChartCard title={t('an_injection')} sub="kWh">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={injection} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
                <Bar dataKey="afname_kwh" name="Afname (kWh)" fill="#0d9488" />
                <Bar dataKey="injectie_kwh" name="Injectie (kWh)" fill="#84cc16" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>
    </>
  );
}
